import React, { useEffect, useMemo, useState } from 'react';
import { CancelIcon } from '../../assets/icons/icons';
import { supabase } from '../../supabaseClient';
import {
  PROTEIN_OPTIONS as PROTEINS,
  CARB_OPTIONS as CARBS,
  FAT_OPTIONS as FATS,
} from '../../constants/nutritionOptions';
import bodyTypeOutcomes from '../../constants/body_type_outcomes.json';
import './style.css';

function formatHeight(assessmentRow) {
  const ft = assessmentRow?.height_feet ?? null;
  const inch = assessmentRow?.height_inches ?? null;
  if (ft == null && inch == null) return null;
  const ftPart = ft != null ? `${ft}′` : '';
  const inchPart = inch != null ? `${inch}″` : '';
  return `${ftPart}${ftPart && inchPart ? ' ' : ''}${inchPart}`.trim() || null;
}

function formatValue(value) {
  if (value == null) return '—';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function pick(obj, path) {
  if (!obj) return null;
  return path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : null), obj);
}

function renderPreferenceList(items = []) {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) return <div className="coach-muted">Not provided yet.</div>;
  return (
    <div className="preference-choice-list readonly">
      {list.map((item) => (
        <div key={item} className="preference-option selected readonly">
          <span className="preference-option-leading">✓</span>
          <span className="preference-option-label">{item}</span>
        </div>
      ))}
    </div>
  );
}

const GOAL_OPTIONS = [
  { value: 'Weight Loss', label: 'Weight Loss' },
  { value: 'Weight Gain', label: 'Weight Gain' },
  { value: 'Muscle Size', label: 'Muscle Size' },
  { value: 'Fatloss', label: 'Fat Loss' },
  { value: 'Health/Longevitiy', label: 'Health/Longevitiy' },
];

const BODY_FAT_RANGES = [
  {
    key: 'range-4-14',
    men: { label: 'Men 4-9%' },
    women: { label: 'Women 10-14%' },
  },
  {
    key: 'range-10-20',
    men: { label: 'Men 10-15%' },
    women: { label: 'Women 15-20%' },
  },
  {
    key: 'range-16-25',
    men: { label: 'Men 16-20%' },
    women: { label: 'Women 21-25%' },
  },
  {
    key: 'range-21-35',
    men: { label: 'Men 21-30%' },
    women: { label: 'Women 26-35%' },
  },
  {
    key: 'range-31-40',
    men: { label: 'Men 31-40%' },
    women: { label: 'Women 36-40%' },
  },
  {
    key: 'range-41-plus',
    men: { label: 'Men 41%+' },
    women: { label: 'Women 41%+' },
  },
];

const PROF_ACTIVITY = ['Sedentary', 'Moderately Active', 'Active', 'Very Active'];

const DESCRIBE = [
  'Sedentary Adult',
  'Exercising Adult',
  'Competive Athlete',
  'Growing Teenage Athlete',
  'Adult Building Muscle',
  'Athlete Restricting Calories',
];

const BODY_TYPE_OPTIONS = Array.from(
  new Set((bodyTypeOutcomes?.body_type_outcomes || []).map((entry) => entry.body_type).filter(Boolean)),
).sort();

const toNumOrNull = (value) => {
  if (value === '' || value == null) return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const IMAGE_BUCKET = process.env.REACT_APP_SUPABASE_IMAGE_BUCKET || 'coach-images';
const SIGNED_URL_TTL = 60 * 60;

const isAbsoluteUrl = (value = '') => (
  value.startsWith('http://') || value.startsWith('https://') || value.startsWith('data:')
);

function normalizeImages(value) {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') {
          return isAbsoluteUrl(item) ? { url: item } : { path: item };
        }
        if (item && typeof item === 'object') {
          const direct = item.url || item.publicUrl || item.dataUrl || item.src || null;
          if (direct) return { url: direct };
          const path = item.path || item.storagePath || item.key || null;
          return path ? { path } : null;
        }
        return null;
      })
      .filter(Boolean);
  }
  return [];
}

export default function CoachAssessmentModal({
  isOpen,
  onClose,
  onUpdated,
  memberName,
  assessmentRow,
  loading = false,
  error = '',
}) {
  const parsedAnswers = useMemo(() => {
    if (!assessmentRow?.assessment) return null;
    if (typeof assessmentRow.assessment === 'string') {
      try {
        return JSON.parse(assessmentRow.assessment);
      } catch {
        return null;
      }
    }
    return assessmentRow.assessment;
  }, [assessmentRow]);
  const answers = parsedAnswers;
  const height = formatHeight(assessmentRow);
  const [imageUrls, setImageUrls] = useState([]);
  const [imageStatus, setImageStatus] = useState('idle');
  const [lightboxSrc, setLightboxSrc] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    name: '',
    age: '',
    gender: '',
    heightFeet: '',
    heightInches: '',
    weightLbs: '',
    primaryGoal: '',
    bodyFat: '',
    bodyType: '',
    professionActivity: '',
    describe: '',
    weeklyActivities: '',
    stepsPerDay: '',
    proteins: [],
    carbs: [],
    fats: [],
  });
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (lightboxSrc) {
        setLightboxSrc('');
        return;
      }
      onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, lightboxSrc, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setImageUrls([]);
      setImageStatus('idle');
      setLightboxSrc('');
      setEditMode(false);
      setSaveError('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const next = parsedAnswers || {};
    const basics = next.basics || {};
    const goals = next.goals || {};
    const workout = next.workout || {};
    const nutrition = next.nutrition || {};
    setForm({
      name: basics.name ?? assessmentRow?.name ?? '',
      age: basics.age ?? assessmentRow?.age ?? '',
      gender: basics.gender ?? assessmentRow?.gender ?? '',
      heightFeet: basics?.height?.feet ?? assessmentRow?.height_feet ?? '',
      heightInches: basics?.height?.inches ?? assessmentRow?.height_inches ?? '',
      weightLbs: basics?.weight_lbs ?? assessmentRow?.weight_lbs ?? '',
      primaryGoal: goals.primary ?? goals.goal_primary ?? goals.primary_goal ?? workout.primary_goal ?? '',
      bodyFat: goals.body_fat ?? goals.bodyfat ?? '',
      bodyType: goals.body_type ?? goals.bodyType ?? '',
      professionActivity: goals.profession_activity ?? goals.professionActivity ?? '',
      describe: goals.describe ?? '',
      weeklyActivities: goals.weekly_activities ?? goals.weeklyActivities ?? '',
      stepsPerDay: goals.steps_per_day ?? goals.stepsPerDay ?? '',
      proteins: Array.isArray(nutrition.proteins) ? nutrition.proteins : (Array.isArray(nutrition.protein) ? nutrition.protein : []),
      carbs: Array.isArray(nutrition.carbs) ? nutrition.carbs : (Array.isArray(nutrition.carb) ? nutrition.carb : []),
      fats: Array.isArray(nutrition.fats) ? nutrition.fats : (Array.isArray(nutrition.fat) ? nutrition.fat : []),
    });
  }, [isOpen, assessmentRow, parsedAnswers]);

  const basics = answers?.basics ?? {};
  const goals = answers?.goals ?? {};
  const nutrition = answers?.nutrition ?? {};
  const imageRefs = useMemo(() => normalizeImages(
    answers?.media?.images || answers?.images || answers?.assessmentImages || answers?.photos
  ), [answers]);

  useEffect(() => {
    let active = true;
    const loadImages = async () => {
      if (!isOpen) return;
      if (!imageRefs.length) {
        if (active) setImageUrls([]);
        return;
      }
      if (active) setImageStatus('loading');
      const urls = await Promise.all(imageRefs.map(async (ref) => {
        if (ref.url) return ref.url;
        if (!ref.path) return null;
        try {
          const { data, error } = await supabase.storage
            .from(IMAGE_BUCKET)
            .createSignedUrl(ref.path, SIGNED_URL_TTL);
          if (error) return null;
          return data?.signedUrl || null;
        } catch {
          return null;
        }
      }));
      if (active) {
        setImageUrls(urls.filter(Boolean));
        setImageStatus('done');
      }
    };
    loadImages();
    return () => { active = false; };
  }, [imageRefs, isOpen]);

  if (!isOpen) return null;

  const genderKey = form.gender === 'Female' ? 'women' : form.gender === 'Male' ? 'men' : null;
  const bodyFatOptions = BODY_FAT_RANGES.map((range) => {
    if (!genderKey) {
      return { key: range.key, label: `${range.men.label} / ${range.women.label}` };
    }
    const entry = range[genderKey];
    return { key: range.key, label: entry?.label || range.men.label };
  });

  const updateForm = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const togglePreference = (key, value) => {
    setForm((prev) => {
      const list = Array.isArray(prev[key]) ? prev[key] : [];
      const next = list.includes(value)
        ? list.filter((item) => item !== value)
        : [...list, value];
      return { ...prev, [key]: next };
    });
  };
  const selectAll = (key, options) => updateForm({ [key]: options.slice() });
  const clearAll = (key) => updateForm({ [key]: [] });

  const handleSave = async () => {
    if (!assessmentRow?.user_id) return;
    setSaveError('');
    const payload = {
      ...(parsedAnswers || {}),
      basics: {
        name: (form.name || '').trim(),
        age: toNumOrNull(form.age),
        gender: form.gender || '',
        height: {
          feet: toNumOrNull(form.heightFeet),
          inches: toNumOrNull(form.heightInches),
        },
        weight_lbs: toNumOrNull(form.weightLbs),
      },
      goals: {
        ...(parsedAnswers?.goals || {}),
        primary: form.primaryGoal || '',
        goal_primary: form.primaryGoal || '',
        primary_goal: form.primaryGoal || '',
        body_fat: form.bodyFat || '',
        bodyfat: form.bodyFat || '',
        body_type: form.bodyType || '',
        bodyType: form.bodyType || '',
        profession_activity: form.professionActivity || '',
        professionActivity: form.professionActivity || '',
        describe: form.describe || '',
        weekly_activities: form.weeklyActivities || '',
        weeklyActivities: form.weeklyActivities || '',
        steps_per_day: toNumOrNull(form.stepsPerDay),
        stepsPerDay: toNumOrNull(form.stepsPerDay),
      },
      workout: {
        ...(parsedAnswers?.workout || {}),
        primary_goal: form.primaryGoal || '',
        goal_primary: form.primaryGoal || '',
      },
      nutrition: {
        proteins: form.proteins || [],
        protein: form.proteins || [],
        carbs: form.carbs || [],
        carb: form.carbs || [],
        fats: form.fats || [],
        fat: form.fats || [],
      },
      created_at: parsedAnswers?.created_at || new Date().toISOString(),
    };
    setSaveLoading(true);
    try {
      const { data, error: saveErr } = await supabase
        .from('assessments')
        .update({
          assessment: payload,
          name: payload.basics.name,
          age: payload.basics.age,
          gender: payload.basics.gender,
          height_feet: payload.basics.height.feet,
          height_inches: payload.basics.height.inches,
          weight_lbs: payload.basics.weight_lbs,
        })
        .eq('user_id', assessmentRow.user_id)
        .select('user_id, name, age, gender, height_feet, height_inches, weight_lbs, assessment')
        .maybeSingle();
      if (saveErr) throw saveErr;
      if (!data) {
        throw new Error('Unable to update assessment. You may not have permission for this member.');
      }
      // Keep assessment save responsive; sync nutrition prefs in the background.
      supabase
        .from('nutrition_preferences')
        .upsert({
          user_id: assessmentRow.user_id,
          proteins: form.proteins || [],
          carbs: form.carbs || [],
          fats: form.fats || [],
        }, { onConflict: 'user_id' })
        .then(() => {})
        .catch(() => {});
      onUpdated?.(data || { ...assessmentRow, assessment: payload }, {
        proteins: form.proteins || [],
        carbs: form.carbs || [],
        fats: form.fats || [],
      });
      setEditMode(false);
    } catch (e) {
      setSaveError(e?.message || 'Unable to update assessment.');
    } finally {
      setSaveLoading(false);
    }
  };

  const qaSections = [
    {
      title: 'Basics',
      rows: [
        { q: 'Name', a: basics?.name ?? assessmentRow?.name ?? memberName },
        { q: 'Age', a: basics?.age ?? assessmentRow?.age },
        { q: 'Gender', a: basics?.gender ?? assessmentRow?.gender },
        { q: 'Height', a: pick(basics, 'height.feet') != null || pick(basics, 'height.inches') != null ? `${pick(basics, 'height.feet') ?? ''}′ ${pick(basics, 'height.inches') ?? ''}″`.trim() : height },
        { q: 'Weight (lbs)', a: basics?.weight_lbs ?? assessmentRow?.weight_lbs },
      ],
    },
    {
      title: 'Goals',
      rows: [
        { q: 'Primary goal', a: goals?.primary },
        { q: 'Body fat', a: goals?.body_fat },
        { q: 'Body type', a: goals?.body_type },
        { q: 'Profession activity', a: goals?.profession_activity },
        { q: 'Describe', a: goals?.describe },
        { q: 'Weekly activities', a: goals?.weekly_activities },
        { q: 'Steps per day', a: goals?.steps_per_day },
      ],
    },
    {
      title: 'Nutrition Preferences',
      rows: [],
    },
  ].filter((s) => s.title !== 'Nutrition Preferences');

  return (
    <div className="coach-modal-overlay" onClick={() => onClose?.()}>
      <div className="coach-modal" onClick={(e) => e.stopPropagation()}>
        <div className="coach-modal-header coach-modal-header-stacked">
          <div className="coach-modal-header-left">
            <div className="coach-modal-title">Assessment</div>
            <div className="coach-muted">{memberName || 'Member'}</div>
          </div>
          <div className="coach-modal-actions">
            <button type="button" className="coach-modal-close" onClick={() => onClose?.()} aria-label="Close">
              <CancelIcon className="coach-modal-close-icon" />
            </button>
            {!loading && !error && assessmentRow && !editMode && (
              <button
                type="button"
                className="coach-btn secondary"
                onClick={() => setEditMode(true)}
              >
                Edit
              </button>
            )}
            {editMode && (
              <div className="coach-modal-action-row">
                <button
                  type="button"
                  className="coach-btn"
                  onClick={handleSave}
                  disabled={saveLoading}
                >
                  {saveLoading ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  className="coach-btn secondary"
                  onClick={() => {
                    setEditMode(false);
                    setSaveError('');
                  }}
                  disabled={saveLoading}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="coach-modal-body">
          {loading ? (
            <div className="coach-muted">Loading assessment...</div>
          ) : error ? (
            <div className="coach-alert error">{error}</div>
          ) : !answers ? (
            <div className="coach-alert">No assessment answers saved yet.</div>
          ) : editMode ? (
            <div className="coach-qa">
              <div className="coach-qa-section">
                <div className="coach-qa-title">Basics</div>
                <div className="coach-form">
                  <div className="coach-grid-2">
                    <div className="coach-row">
                      <label className="coach-label">Name</label>
                      <input className="coach-input" value={form.name} onChange={(e) => updateForm({ name: e.target.value })} />
                    </div>
                    <div className="coach-row">
                      <label className="coach-label">Age</label>
                      <input className="coach-input" type="number" min={0} value={form.age} onChange={(e) => updateForm({ age: e.target.value })} />
                    </div>
                    <div className="coach-row">
                      <label className="coach-label">Gender</label>
                      <select className="coach-input" value={form.gender} onChange={(e) => updateForm({ gender: e.target.value })}>
                        <option value="">Select</option>
                        <option>Male</option>
                        <option>Female</option>
                      </select>
                    </div>
                    <div className="coach-row">
                      <label className="coach-label">Weight (lbs)</label>
                      <input className="coach-input" type="number" min={0} value={form.weightLbs} onChange={(e) => updateForm({ weightLbs: e.target.value })} />
                    </div>
                  </div>
                  <div className="coach-grid-2">
                    <div className="coach-row">
                      <label className="coach-label">Height (feet)</label>
                      <input className="coach-input" type="number" min={0} max={8} value={form.heightFeet} onChange={(e) => updateForm({ heightFeet: e.target.value })} />
                    </div>
                    <div className="coach-row">
                      <label className="coach-label">Height (inches)</label>
                      <input className="coach-input" type="number" min={0} max={11} value={form.heightInches} onChange={(e) => updateForm({ heightInches: e.target.value })} />
                    </div>
                  </div>
                </div>
              </div>

              <div className="coach-qa-section">
                <div className="coach-qa-title">Goals</div>
                <div className="coach-form">
                  <div className="coach-grid-2">
                    <div className="coach-row">
                      <label className="coach-label">Primary goal</label>
                      <select className="coach-input" value={form.primaryGoal} onChange={(e) => updateForm({ primaryGoal: e.target.value })}>
                        <option value="">Select</option>
                        {GOAL_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="coach-row">
                      <label className="coach-label">Body fat</label>
                      <select className="coach-input" value={form.bodyFat} onChange={(e) => updateForm({ bodyFat: e.target.value })}>
                        <option value="">Select</option>
                        {bodyFatOptions.map((opt) => (
                          <option key={opt.key} value={opt.label}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="coach-row">
                      <label className="coach-label">Body type</label>
                      <select className="coach-input" value={form.bodyType} onChange={(e) => updateForm({ bodyType: e.target.value })}>
                        <option value="">Select</option>
                        {BODY_TYPE_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                    <div className="coach-row">
                      <label className="coach-label">Profession activity</label>
                      <select className="coach-input" value={form.professionActivity} onChange={(e) => updateForm({ professionActivity: e.target.value })}>
                        <option value="">Select</option>
                        {PROF_ACTIVITY.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="coach-grid-2">
                    <div className="coach-row">
                      <label className="coach-label">Describe</label>
                      <select className="coach-input" value={form.describe} onChange={(e) => updateForm({ describe: e.target.value })}>
                        <option value="">Select</option>
                        {DESCRIBE.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                    <div className="coach-row">
                      <label className="coach-label">Steps per day</label>
                      <input className="coach-input" type="number" min={0} value={form.stepsPerDay} onChange={(e) => updateForm({ stepsPerDay: e.target.value })} />
                    </div>
                  </div>
                  <div className="coach-row">
                    <label className="coach-label">Weekly activities</label>
                    <textarea
                      className="coach-input coach-textarea"
                      value={form.weeklyActivities}
                      onChange={(e) => updateForm({ weeklyActivities: e.target.value })}
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              <div className="coach-qa-section">
                <div className="coach-qa-title">Nutrition Preferences</div>
                <div className="coach-pref-block">
                  <div className="coach-pref-title">Preferred proteins</div>
                  <div className="coach-actions" style={{ marginBottom: 6 }}>
                    <button type="button" className="coach-btn secondary" onClick={() => selectAll('proteins', PROTEINS)}>Select all</button>
                    <button type="button" className="coach-btn secondary" onClick={() => clearAll('proteins')}>Clear</button>
                  </div>
                  <div className="preference-choice-list">
                    {PROTEINS.map((item) => (
                      <button
                        key={item}
                        type="button"
                        className={`preference-option ${form.proteins.includes(item) ? 'selected' : ''}`}
                        onClick={() => togglePreference('proteins', item)}
                      >
                        <span className="preference-option-leading">{form.proteins.includes(item) ? '✓' : '○'}</span>
                        <span className="preference-option-label">{item}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="coach-pref-block">
                  <div className="coach-pref-title">Preferred carbs</div>
                  <div className="coach-actions" style={{ marginBottom: 6 }}>
                    <button type="button" className="coach-btn secondary" onClick={() => selectAll('carbs', CARBS)}>Select all</button>
                    <button type="button" className="coach-btn secondary" onClick={() => clearAll('carbs')}>Clear</button>
                  </div>
                  <div className="preference-choice-list">
                    {CARBS.map((item) => (
                      <button
                        key={item}
                        type="button"
                        className={`preference-option ${form.carbs.includes(item) ? 'selected' : ''}`}
                        onClick={() => togglePreference('carbs', item)}
                      >
                        <span className="preference-option-leading">{form.carbs.includes(item) ? '✓' : '○'}</span>
                        <span className="preference-option-label">{item}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="coach-pref-block">
                  <div className="coach-pref-title">Preferred fats</div>
                  <div className="coach-actions" style={{ marginBottom: 6 }}>
                    <button type="button" className="coach-btn secondary" onClick={() => selectAll('fats', FATS)}>Select all</button>
                    <button type="button" className="coach-btn secondary" onClick={() => clearAll('fats')}>Clear</button>
                  </div>
                  <div className="preference-choice-list">
                    {FATS.map((item) => (
                      <button
                        key={item}
                        type="button"
                        className={`preference-option ${form.fats.includes(item) ? 'selected' : ''}`}
                        onClick={() => togglePreference('fats', item)}
                      >
                        <span className="preference-option-leading">{form.fats.includes(item) ? '✓' : '○'}</span>
                        <span className="preference-option-label">{item}</span>
                      </button>
                    ))}
                  </div>
                </div>
                {saveError && <div className="coach-alert error" style={{ marginTop: 8 }}>{saveError}</div>}
              </div>
            </div>
          ) : (
            <>
              <div className="coach-qa">
                {qaSections.map((section) => (
                  <div key={section.title} className="coach-qa-section">
                    <div className="coach-qa-title">{section.title}</div>
                    <div className="coach-qa-list">
                      {section.rows.filter((r) => r.q).map((row) => (
                        <div key={row.q} className="coach-qa-row">
                          <div className="coach-qa-q">{row.q}</div>
                          <div className="coach-qa-a">{formatValue(row.a)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="coach-qa-section" style={{ marginTop: 12 }}>
                <div className="coach-qa-title">Nutrition Preferences</div>
                <div className="coach-pref-block">
                  <div className="coach-pref-title">Preferred proteins</div>
                  {renderPreferenceList(nutrition?.proteins)}
                </div>
                <div className="coach-pref-block">
                  <div className="coach-pref-title">Preferred carbs</div>
                  {renderPreferenceList(nutrition?.carbs)}
                </div>
                <div className="coach-pref-block">
                  <div className="coach-pref-title">Preferred fats</div>
                  {renderPreferenceList(nutrition?.fats)}
                </div>
              </div>

              <div className="coach-qa-section" style={{ marginTop: 12 }}>
                <div className="coach-qa-title">Assessment Photos</div>
                {imageRefs.length === 0 ? (
                  <div className="coach-muted">No photos uploaded yet.</div>
                ) : imageStatus === 'loading' ? (
                  <div className="coach-muted">Loading photos...</div>
                ) : imageUrls.length === 0 ? (
                  <div className="coach-muted">Photos are unavailable.</div>
                ) : (
                  <div className="coach-image-grid">
                    {imageUrls.map((src, idx) => (
                      <img
                        key={`assessment-photo-${idx}`}
                        src={src}
                        alt={`Assessment photo ${idx + 1}`}
                        className="coach-image-thumb"
                        loading="lazy"
                        onClick={() => setLightboxSrc(src)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') setLightboxSrc(src);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {lightboxSrc && (
        <div
          className="coach-image-lightbox"
          onClick={(e) => {
            e.stopPropagation();
            setLightboxSrc('');
          }}
        >
          <button
            type="button"
            className="coach-image-lightbox-close"
            aria-label="Close image"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxSrc('');
            }}
          >
            ×
          </button>
          <img src={lightboxSrc} alt="Full size assessment" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
